import { boardInsertionIndex } from "../features/tasks/board-reorder.ts";
import { edgeAutoScroll } from "./gesture.ts";

export interface DragListActions {
  dropped?(originalOrder: HTMLElement[]): void | Promise<void>;
  hapticFeedback?(): void;
  reducedMotion?(): boolean;
}

export function bindDragList(list: HTMLElement, selector: string, actions: DragListActions = {}): void {
  let dragged: HTMLElement | null = null;
  let originalOrder: HTMLElement[] = [];
  let dropped = false;
  let pendingPointerY: number | null = null;
  let placementFrame: number | null = null;
  const movementAnimations = new WeakMap<HTMLElement, Animation>();

  const clearDragState = () => {
    if (placementFrame !== null) cancelAnimationFrame(placementFrame);
    placementFrame = null;
    pendingPointerY = null;
    list.classList.remove("is-drag-active");
    list.querySelectorAll(".is-drop-target").forEach(item => item.classList.remove("is-drop-target"));
  };
  const placeDraggedItem = (pointerY: number) => {
    if (!dragged) return;
    const candidates = Array.from(list.querySelectorAll<HTMLElement>(`:scope > ${selector}`)).filter(item => item !== dragged);
    const insertionIndex = boardInsertionIndex(pointerY, candidates.map(item => {
      const bounds = item.getBoundingClientRect();
      return bounds.top + bounds.height / 2;
    }));
    const before = candidates[insertionIndex] || null;
    if (before ? dragged.nextElementSibling === before : list.lastElementChild === dragged) return;
    const items = Array.from(list.querySelectorAll<HTMLElement>(`:scope > ${selector}`));
    const previousBounds = new Map(items.map(item => [item, item.getBoundingClientRect()]));
    list.insertBefore(dragged, before);
    list.querySelectorAll(".is-drop-target").forEach(item => item.classList.remove("is-drop-target"));
    (before || candidates.at(-1))?.classList.add("is-drop-target");
    if (actions.reducedMotion?.() ?? window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    items.forEach(item => {
      if (item === dragged || !item.isConnected) return;
      const previous = previousBounds.get(item);
      if (!previous) return;
      const current = item.getBoundingClientRect();
      const x = previous.left - current.left;
      const y = previous.top - current.top;
      if (Math.abs(x) < 1 && Math.abs(y) < 1) return;
      movementAnimations.get(item)?.cancel();
      movementAnimations.set(item, item.animate(
        [{ transform: `translate3d(${x}px,${y}px,0)` }, { transform: "translate3d(0,0,0)" }],
        { duration: 140, easing: "cubic-bezier(.2,.8,.2,1)" },
      ));
    });
  };
  const schedulePlacement = (pointerY: number) => {
    pendingPointerY = pointerY;
    if (placementFrame !== null) return;
    placementFrame = requestAnimationFrame(() => {
      placementFrame = null;
      const nextPointerY = pendingPointerY;
      pendingPointerY = null;
      if (nextPointerY !== null) placeDraggedItem(nextPointerY);
    });
  };

  list.addEventListener("dragstart", event => {
    const item = (event.target as HTMLElement).closest<HTMLElement>(selector);
    if (!item || item.parentElement !== list) return;
    dragged = item;
    originalOrder = Array.from(list.querySelectorAll<HTMLElement>(`:scope > ${selector}`));
    dropped = false;
    item.classList.add("is-dragging");
    list.classList.add("is-drag-active");
    if (event.dataTransfer) event.dataTransfer.effectAllowed = "move";
    event.dataTransfer?.setData("text/plain", item.dataset.todo || item.dataset.subtask || "");
  });
  list.addEventListener("dragend", () => {
    if (!dropped && dragged) {
      originalOrder.forEach(item => list.append(item));
      const cancelled = dragged;
      cancelled.classList.add("is-drag-cancelled");
      window.setTimeout(() => cancelled.classList.remove("is-drag-cancelled"), 180);
    }
    dragged?.classList.remove("is-dragging");
    clearDragState();
    dragged = null;
  });
  list.addEventListener("dragover", event => {
    event.preventDefault();
    if (!dragged) return;
    if (event.dataTransfer) event.dataTransfer.dropEffect = "move";
    schedulePlacement(event.clientY);
    const bounds = list.getBoundingClientRect();
    list.scrollTop += edgeAutoScroll(event.clientY, bounds.top, bounds.bottom);
  });
  list.addEventListener("drop", event => {
    event.preventDefault();
    if (!dragged) return;
    dropped = true;
    if (placementFrame !== null) cancelAnimationFrame(placementFrame);
    placementFrame = null;
    pendingPointerY = null;
    placeDraggedItem(event.clientY);
    clearDragState();
    actions.hapticFeedback?.();
    if (actions.dropped) void actions.dropped(originalOrder);
  });
}
